import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiUrl } from "../../../utils/api";
import type { DocumentDto } from "../../../services/documentService";
import { KundePicker, kontaktName, type KundeAuswahl } from "../../calendar/KundePicker";
import { useDokumentReiter, type DokumentReiter } from "./useDokumentReiter";
import { OrdnerNavigator } from "../explorer/OrdnerNavigator";
import {
    KATEGORIE_FILTER,
    STANDARD_AUSGESCHLOSSENE_KATEGORIEN,
    dateiTyp,
    formatiereAenderung,
    formatiereGroesse,
    gruppiereNachOrdner,
    gruppiereNachVorgang,
    mitDiesemVorgang,
    naechsteAuswahl,
    passtZuAccept,
} from "./dokumentAuswahlDaten";
import { ausDateiFeld, ausDrop, eintraegeAusDrop, type RechnerDatei } from "./rechnerAblage";
import "./dokumentAuswahl.css";
import { alleFenster, elementMitId, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

/**
 * Die Auswahl eines Dokuments — aus der Kundenakte, aus einem Vorgang oder
 * vom Rechner.
 *
 * ══ WARUM EINE FLÄCHE OHNE HÜLLE ═══════════════════════════════════════════
 * Dieselbe Auswahl steht an vier Stellen (Mail-Composer, Vorgang V4,
 * Vollmacht-Chip, „Datei scannen"), und die vier leben in drei
 * unvereinbaren Dialog-Welten: WindowManager-Fenster mit Fluent, `.vg4-dialog`
 * im Vorgang, `PopupSchale` in den Kontaktdetails. Eine Komponente, die ihren
 * eigenen Schleier mitbrächte, passte in keine davon; deshalb liefert sie nur
 * das Innere (Reiter, Liste, Fuß) und füllt die Höhe ihrer Hülle.
 * Vorbild ist `ListenFlaeche` in `AlleListeDialog.tsx`.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ WIE EIN DATEIDIALOG DES SYSTEMS (PO 28.09.2026) ════════════════════════
 * Keine Überschrift, kein „Kunde ändern"-Kopf, keine Radio-Buttons oder
 * Checkboxen: Die Reiter stehen ganz oben, darunter eine Detailliste
 * (Name · Geändert · Typ · Größe) mit der Bedienung des Explorers — Klick
 * markiert, Strg+Klick ergänzt, Umschalt+Klick markiert einen Bereich,
 * Doppelklick übernimmt. Der Kunde steht als Pfad in der Werkzeugleiste
 * („Kunden › Müller, Stefan › Dokumente"); ein Klick auf „Kunden" wählt
 * einen anderen.
 *
 * „Vom Rechner" nimmt gezogene Dateien UND Ordner an und öffnet auf Knopfdruck
 * den Explorer (Dateien oder ganzer Ordner) — Details in `rechnerAblage.ts`.
 * Oben rechts schließt ein ✕; die Hüllen lassen sich über die Ecke unten
 * rechts größer und kleiner ziehen (`.dok-aw-huelle`).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die drei Quellen stehen als Reiter nebeneinander, nicht in einer gemischten
 * Liste: an einem Kontakt hängen bis zu fünfstellig viele Dateien der Alten
 * Kundenakte, zwischen denen die drei Dokumente des laufenden Vorgangs sonst
 * untergehen.
 *
 * T-225: Kennt der Wirt den Vorgang (Antwort auf eine Vorgangs-Mail, neue
 * Mail aus dem Vorgang), stehen dessen Dokumente im Reiter „Aus Vorgängen"
 * als erste Gruppe „Dieser Vorgang" — unabhängig davon, wer als Kunde gewählt
 * ist, und auch bei einem Vorgang ohne Kunden. Die Auswahl startet dann dort.
 *
 * Die Auswahl lebt über den Reitern: wer ein Kundendokument, ein
 * Vorgangsdokument und eine Datei vom Rechner zusammen übernehmen will, soll
 * das in einem Rutsch tun können.
 */

export type DokumentAuswahlErgebnis = {
    /** Bereits in KMVS liegende Dokumente. */
    dokumente: DocumentDto[];
    /** Dateien vom Rechner — noch nicht hochgeladen. */
    dateien: File[];
};

export type DokumentAuswahlProps = {
    /** Kunde, dessen Akte und Vorgänge angeboten werden. null → Picker in der Werkzeugleiste. */
    contactId: number | null;
    /**
     * Sein Name für den Pfad. Fehlt er, wird er nachgeladen — die Wirte
     * kennen ihn meist schon (Vorgang, Kontaktdetails) und sparen den Abruf.
     */
    kundeName?: string | null;
    /**
     * T-225: Der Vorgang, aus dem heraus gearbeitet wird. Nur Anzeige — seine
     * Dokumente stehen in „Aus Vorgängen" oben; verknüpft wird damit nichts.
     */
    vorgangId?: number | null;
    /** Titel des Vorgangs für die Gruppe „Dieser Vorgang" (optional). */
    vorgangTitel?: string | null;
    /** Nur ein Treffer bzw. eine Datei wählbar (Scan, Vollmacht). */
    einzeln?: boolean;
    /**
     * Kunde nicht umstellbar (Vollmacht im Aktenkopf, T-42): Nur Dokumente
     * DIESES Kontakts sind verknüpfbar — das Backend lehnt fremde mit 404 ab.
     * Der Pfad zeigt dann kein „Kunden ›" zum Wechseln. Greift nur mit
     * `contactId`; ohne Kunden bleibt der Picker.
     */
    kundeFest?: boolean;
    /**
     * Dateitypen wie im `accept`-Attribut. In allen Reitern werden Dateien mit
     * anderem Format AUSGEGRAUT statt ausgefiltert — sonst stimmte die
     * Trefferzahl im Reiter nicht mehr mit dem überein, was der Server
     * gezählt hat.
     */
    accept?: string;
    /** Dokumente, die am Ziel schon hängen: sichtbar, aber nicht wählbar. */
    ausschluss?: ReadonlySet<number>;
    /** Kategorien, die gar nicht angeboten werden. */
    ausgeschlosseneKategorien?: readonly string[];
    /** Beschriftung des Übernehmen-Knopfes („Anhängen", „Übernehmen"). */
    uebernehmenText?: string;
    onUebernehmen: (auswahl: DokumentAuswahlErgebnis) => void;
    onAbbrechen: () => void;
    /**
     * Im WindowManager-Fenster: kein ✕ oben rechts und kein Zieh-Griff — das
     * Fenster hat Schließen-Knopf und Größenänderung selbst.
     */
    imFenster?: boolean;
};

type ReiterName = "kunde" | "akte" | "vorgaenge" | "rechner";

/** Was hinter einem Auswahl-Schlüssel steht. */
type Eintrag = { art: "dok"; doc: DocumentDto } | { art: "datei"; datei: File };

/** Eine Zeile der Liste — gleich gebaut für KMVS-Dokumente und Dateien. */
type Zeile = {
    key: string;
    name: string;
    geaendert: string;
    typ: string;
    groesse: string;
    /** Warum nicht wählbar; null = wählbar. */
    sperre: "dabei" | "format" | null;
    titel: string;
    eintrag: Eintrag;
};

type Gruppe = {
    key: string;
    /** Leer = Zeilen ohne Kopfzeile (Wurzel der Akte, ein Rechner-Ordner). */
    titel: string;
    unterzeile?: string;
    zeilen: Zeile[];
};

const dokKey = (id: number) => `dok:${id}`;

export const DokumentAuswahl: React.FC<DokumentAuswahlProps> = ({
    contactId,
    kundeName,
    vorgangId = null,
    vorgangTitel = null,
    einzeln = false,
    kundeFest = false,
    accept,
    ausschluss,
    ausgeschlosseneKategorien = STANDARD_AUSGESCHLOSSENE_KATEGORIEN,
    uebernehmenText = "Übernehmen",
    onUebernehmen,
    onAbbrechen,
    imFenster = false,
}) => {
    // Der Kunde ist umstellbar — im Scan-Dialog gibt es beim Öffnen noch gar
    // keinen, und in der Mail kann die angehängte Datei von einem anderen
    // Kontakt kommen als dem des Empfängers.
    const [kunde, setKunde] = useState<KundeAuswahl | null>(
        contactId != null ? { id: contactId, name: kundeName ?? "" } : null,
    );
    const [pickerOffen, setPickerOffen] = useState(false);
    const [reiter, setReiter] = useState<ReiterName>(
        vorgangId != null ? "vorgaenge" : contactId == null ? "rechner" : "kunde",
    );

    // Namen nachladen, wenn der Wirt nur die Id kannte.
    useEffect(() => {
        if (kunde == null || kunde.name) return;
        const ctrl = new AbortController();
        const id = kunde.id;
        fetch(apiUrl(`contact/${id}`), { signal: ctrl.signal })
            .then((r) => (r.ok ? r.json() : null))
            .then((c) => {
                if (!c) return;
                setKunde((k) => (k && k.id === id ? { id, name: kontaktName(c) } : k));
            })
            .catch(() => {
                /* Ohne Namen steht im Pfad nur „Kontakt 42" — kein Grund für eine Meldung. */
            });
        return () => ctrl.abort();
    }, [kunde]);

    // ── Größe der Hülle ─────────────────────────────────────────────────────
    const wurzel = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (imFenster) return;
        const huelle = wurzel.current?.closest<HTMLElement>(".dok-aw-huelle");
        const gemerkt = leseGroesse();
        if (huelle && gemerkt) setzeGroesse(huelle, gemerkt.b, gemerkt.h);
    }, [imFenster]);

    const kundeId = kunde?.id ?? null;
    const kundenReiter = useDokumentReiter({
        contactId: kundeId,
        nurVorgangsdokumente: false,
        ausgeschlosseneKategorien,
    });
    const vorgangsReiter = useDokumentReiter({
        contactId: kundeId,
        nurVorgangsdokumente: true,
        ausgeschlosseneKategorien,
    });
    // T-225: die Dokumente genau des laufenden Vorgangs — ohne Vorgang leer
    // und ohne Abruf.
    const dieserVorgang = useDokumentReiter({
        contactId: null,
        vorgangId,
        nurVorgangsdokumente: true,
        ausgeschlosseneKategorien,
    });
    const mitVorgang = vorgangId != null;
    // Reiter „Kundenakte" (PO 06.10.2026): die Ordnerstruktur der Akte zum
    // Durchklicken — nur die echten Ordner, ohne den virtuellen KMVS-Ordner des
    // Dokumente-Reiters. `aktenPfad` ist der geöffnete Ordner ("" = Wurzel).
    const [aktenPfad, setAktenPfad] = useState("");
    useEffect(() => setAktenPfad(""), [kundeId]);
    const aktenReiter = useDokumentReiter({
        contactId: reiter === "akte" ? kundeId : null,
        nurVorgangsdokumente: false,
        ausgeschlosseneKategorien,
        aktenOrdner: aktenPfad,
    });
    const kmvsReiter: DokumentReiter | null =
        reiter === "kunde"
            ? kundenReiter
            : reiter === "akte"
              ? aktenReiter
              : reiter === "vorgaenge"
                ? vorgangsReiter
                : null;

    // ── Auswahl ────────────────────────────────────────────────────────────
    const [auswahl, setAuswahl] = useState<Map<string, Eintrag>>(() => new Map());
    const [anker, setAnker] = useState<string | null>(null);
    const [fokus, setFokus] = useState<string | null>(null);
    const [zugeklappt, setZugeklappt] = useState<ReadonlySet<string>>(() => new Set());

    // ── Vom Rechner ────────────────────────────────────────────────────────
    /** Gezogene oder im Explorer gewählte Dateien, die jüngste Ablage oben. */
    const [hinzugefuegt, setHinzugefuegt] = useState<(RechnerDatei & { key: string })[]>([]);
    const neuZaehler = useRef(0);
    const [rechnerSuche, setRechnerSuche] = useState("");
    const [ziehen, setZiehen] = useState(false);
    const [liest, setLiest] = useState(false);
    const dateiFeld = useRef<HTMLInputElement>(null);
    const ordnerFeld = useRef<HTMLInputElement>(null);

    const nimmDateien = useCallback(
        (neue: RechnerDatei[]) => {
            if (neue.length === 0) return;
            const liste = (einzeln ? neue.slice(0, 1) : neue).map((e) => ({
                ...e,
                key: `neu:${++neuZaehler.current}`,
            }));
            setHinzugefuegt((vorher) => [...liste, ...vorher]);
            setAuswahl((vorher) => {
                // Eine Wahl ersetzt bei `einzeln` jede vorige — auch ein
                // KMVS-Dokument, sonst hätte „nur eines" zwei Ergebnisse.
                const naechste = einzeln ? new Map<string, Eintrag>() : new Map(vorher);
                liste.forEach((e) => naechste.set(e.key, { art: "datei", datei: e.datei }));
                return naechste;
            });
            setReiter("rechner");
        },
        [einzeln],
    );

    const listeLeeren = () => {
        setAuswahl((vorher) => {
            const naechste = new Map<string, Eintrag>();
            vorher.forEach((e, key) => {
                if (e.art === "dok") naechste.set(key, e);
            });
            return naechste;
        });
        setHinzugefuegt([]);
        setRechnerSuche("");
    };

    // ── Zeilen des aktiven Reiters ─────────────────────────────────────────
    const kundenGruppen = useMemo(
        () => gruppiereNachOrdner(kundenReiter.zeilen),
        [kundenReiter.zeilen],
    );
    const vorgangsGruppen = useMemo(
        () =>
            mitVorgang
                ? mitDiesemVorgang(
                      dieserVorgang.zeilen,
                      gruppiereNachVorgang(vorgangsReiter.zeilen),
                      vorgangTitel ?? undefined,
                  )
                : gruppiereNachVorgang(vorgangsReiter.zeilen),
        [mitVorgang, dieserVorgang.zeilen, vorgangsReiter.zeilen, vorgangTitel],
    );
    /** Reiterzahl „Aus Vorgängen": mit Vorgang ohne doppelt gezählte Dateien. */
    const vorgaengeZahl = (() => {
        if (!mitVorgang) return kundeId != null ? vorgangsReiter.gesamt : null;
        const schon = new Set(dieserVorgang.zeilen.map((d) => d.id));
        const doppelt = vorgangsReiter.zeilen.filter((d) => schon.has(d.id)).length;
        return dieserVorgang.gesamt + (kundeId != null ? Math.max(0, vorgangsReiter.gesamt - doppelt) : 0);
    })();
    // Im geöffneten Ordner liegen alle Dateien nebeneinander — eine Gruppe ohne
    // Kopfzeile. Erst die Suche greift in die Unterordner und braucht sie.
    const aktenGruppen = useMemo(
        () =>
            aktenReiter.suche.trim()
                ? gruppiereNachOrdner(aktenReiter.zeilen)
                : aktenReiter.zeilen.length
                  ? [{ key: "akte", titel: "", dokumente: aktenReiter.zeilen }]
                  : [],
        [aktenReiter.zeilen, aktenReiter.suche],
    );

    const gruppen: Gruppe[] = useMemo(() => {
        if (reiter === "rechner") {
            const q = rechnerSuche.trim().toLowerCase();
            const passt = (f: File) => !q || f.name.toLowerCase().includes(q);
            const alsZeile = (key: string, datei: File): Zeile => {
                const ok = passtZuAccept({ fileName: datei.name, mimeType: datei.type }, accept);
                return {
                    key,
                    name: datei.name,
                    geaendert: formatiereAenderung(datei.lastModified),
                    typ: dateiTyp(datei.name, datei.type),
                    groesse: formatiereGroesse(datei.size),
                    sperre: ok ? null : "format",
                    titel: ok ? datei.name : "Dieses Format passt hier nicht",
                    eintrag: { art: "datei", datei },
                };
            };
            // Lose Dateien ohne Kopfzeile oben, darunter je gezogenem Ordner
            // eine Gruppe — in der Reihenfolge, in der sie ankamen.
            const liste: Gruppe[] = [];
            const nachOrdner = new Map<string, Gruppe>();
            for (const e of hinzugefuegt) {
                if (!passt(e.datei)) continue;
                let g = nachOrdner.get(e.ordner);
                if (!g) {
                    g = { key: `rechner:${e.ordner}`, titel: e.ordner, zeilen: [] };
                    nachOrdner.set(e.ordner, g);
                    if (e.ordner) liste.push(g);
                    else liste.unshift(g);
                }
                g.zeilen.push(alsZeile(e.key, e.datei));
            }
            return liste;
        }
        const quelle = reiter === "kunde" ? kundenGruppen : reiter === "akte" ? aktenGruppen : vorgangsGruppen;
        return quelle.map((g) => ({
            key: g.key,
            titel: g.titel,
            unterzeile: g.unterzeile,
            zeilen: g.dokumente.map((doc): Zeile => {
                const schonDabei = ausschluss?.has(doc.id) ?? false;
                const formatPasst = passtZuAccept(doc, accept);
                // Der Ordner steht schon in der Kopfzeile — in der Zeile
                // bleibt der reine Dateiname.
                const kurz = (doc.fileName ?? "").split("/").pop() || doc.fileName || "";
                return {
                    key: dokKey(doc.id),
                    name: kurz,
                    geaendert: formatiereAenderung(doc.uploadedAt),
                    typ: dateiTyp(doc.fileName, doc.mimeType),
                    groesse: formatiereGroesse(doc.fileSize),
                    sperre: schonDabei ? "dabei" : formatPasst ? null : "format",
                    titel: schonDabei
                        ? "Hängt hier schon"
                        : formatPasst
                          ? (doc.fileName ?? kurz)
                          : "Dieses Format passt hier nicht",
                    eintrag: { art: "dok", doc },
                };
            }),
        }));
    }, [
        reiter,
        rechnerSuche,
        hinzugefuegt,
        kundenGruppen,
        aktenGruppen,
        vorgangsGruppen,
        ausschluss,
        accept,
    ]);

    /** Die wählbaren Zeilen in Reihenfolge — für Umschalt-Bereiche und Pfeiltasten. */
    const sichtbar = useMemo(
        () =>
            gruppen
                .filter((g) => !(g.titel && zugeklappt.has(g.key)))
                .flatMap((g) => g.zeilen)
                .filter((z) => z.sperre == null),
        [gruppen, zugeklappt],
    );
    const sichtbareKeys = useMemo(() => sichtbar.map((z) => z.key), [sichtbar]);

    const waehle = (zeile: Zeile, art: { strg?: boolean; umschalt?: boolean }) => {
        if (zeile.sperre) return;
        const keys = naechsteAuswahl(new Set(auswahl.keys()), zeile.key, sichtbareKeys, anker, {
            ...art,
            einzeln,
        });
        const naechste = new Map<string, Eintrag>();
        keys.forEach((k) => {
            const e = auswahl.get(k) ?? sichtbar.find((z) => z.key === k)?.eintrag;
            if (e) naechste.set(k, e);
        });
        setAuswahl(naechste);
        if (!art.umschalt) setAnker(zeile.key);
        setFokus(zeile.key);
    };

    const ergebnis = (m: Map<string, Eintrag>): DokumentAuswahlErgebnis => {
        const dokumente: DocumentDto[] = [];
        const dateien: File[] = [];
        m.forEach((e) => (e.art === "dok" ? dokumente.push(e.doc) : dateien.push(e.datei)));
        return { dokumente, dateien };
    };

    const uebernehmen = () => {
        if (auswahl.size === 0) return;
        onUebernehmen(ergebnis(auswahl));
    };

    const onTaste = (e: React.KeyboardEvent) => {
        if (sichtbar.length === 0) return;
        const i = fokus ? sichtbareKeys.indexOf(fokus) : -1;
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const j =
                i < 0
                    ? 0
                    : Math.max(0, Math.min(sichtbar.length - 1, i + (e.key === "ArrowDown" ? 1 : -1)));
            const zeile = sichtbar[j];
            if (e.ctrlKey || e.metaKey) setFokus(zeile.key);
            else waehle(zeile, { umschalt: e.shiftKey && !einzeln });
            elementMitId(zeilenId(zeile.key))?.scrollIntoView?.({ block: "nearest" });
        } else if (e.key === " " && fokus) {
            e.preventDefault();
            const zeile = sichtbar[i];
            if (zeile) waehle(zeile, { strg: true });
        } else if (e.key === "Enter" && auswahl.size > 0) {
            e.preventDefault();
            uebernehmen();
        } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a" && !einzeln) {
            e.preventDefault();
            setAuswahl((vorher) => {
                const naechste = new Map(vorher);
                sichtbar.forEach((z) => naechste.set(z.key, z.eintrag));
                return naechste;
            });
        }
    };

    const wechsleReiter = (r: ReiterName) => {
        setReiter(r);
        setFokus(null);
        setAnker(null);
        setPickerOffen(false);
    };

    // ── Werkzeugleiste ─────────────────────────────────────────────────────
    const pfad = (() => {
        if (reiter === "rechner") {
            return (
                <div className="dok-aw-pfad">
                    <span>Weitere Dateien oder Ordner hierher ziehen</span>
                </div>
            );
        }
        if (!kunde || pickerOffen) {
            return (
                <div className="dok-aw-pfad-picker">
                    <div className="dok-aw-pfad-picker-feld">
                        <KundePicker
                            value={kunde}
                            onChange={(k) => {
                                if (!k) return;
                                setKunde(k);
                                setPickerOffen(false);
                                setAuswahl((vorher) => {
                                    // Dokumente des bisherigen Kunden fallen raus;
                                    // Dateien vom Rechner gehören keinem Kunden.
                                    const naechste = new Map<string, Eintrag>();
                                    vorher.forEach((e, key) => {
                                        if (e.art === "datei") naechste.set(key, e);
                                    });
                                    return naechste;
                                });
                            }}
                            optional
                            label="Kunde"
                        />
                    </div>
                    {kunde && (
                        <button
                            type="button"
                            className="dok-aw-knopf"
                            onClick={() => setPickerOffen(false)}
                        >
                            Abbrechen
                        </button>
                    )}
                </div>
            );
        }
        return (
            <div className="dok-aw-pfad">
                {!kundeFest && (
                    <>
                        <button
                            type="button"
                            title="Anderen Kunden wählen"
                            onClick={() => setPickerOffen(true)}
                        >
                            Kunden
                        </button>
                        <span className="dok-aw-pfad-sep">›</span>
                    </>
                )}
                <span>{kunde.name || `Kontakt ${kunde.id}`}</span>
                <span className="dok-aw-pfad-sep">›</span>
                <span className="letzt">
                    {reiter === "kunde" ? "Dokumente" : reiter === "akte" ? "Kundenakte" : "Vorgänge"}
                </span>
            </div>
        );
    })();

    const suche = reiter === "rechner" ? rechnerSuche : (kmvsReiter?.suche ?? "");
    // „Dieser Vorgang" sucht und filtert immer mit — sonst passten die beiden
    // Teile des Reiters nicht zusammen.
    const setzeSuche = (s: string) => {
        if (reiter === "rechner") setRechnerSuche(s);
        else kmvsReiter?.setSuche(s);
        if (reiter === "vorgaenge") dieserVorgang.setSuche(s);
    };
    const setzeKategorie = (k: string) => {
        kmvsReiter?.setKategorie(k);
        if (reiter === "vorgaenge") dieserVorgang.setKategorie(k);
    };
    // Mit Vorgang hat „Aus Vorgängen" auch ohne Kunden etwas zu zeigen.
    const hatListe = kundeId != null || (reiter === "vorgaenge" && mitVorgang);
    const zeigeSuche = reiter === "rechner" ? hinzugefuegt.length > 0 : hatListe && !pickerOffen;
    // „Vom Rechner" ohne Dateien: keine Leiste, die große Ablage hat alles.
    const zeigeWerkzeuge = reiter !== "rechner" || hinzugefuegt.length > 0;

    // ── Liste ──────────────────────────────────────────────────────────────
    const hinweisOhneZeilen = (() => {
        if (reiter === "rechner") return null;
        if (reiter === "vorgaenge" && mitVorgang) {
            const da = dieserVorgang.zeilen.length + (kundeId != null ? vorgangsReiter.zeilen.length : 0);
            if (da > 0 || dieserVorgang.fehler) return null;
            if (dieserVorgang.laedt || (kundeId != null && vorgangsReiter.laedt)) return "Wird geladen…";
            return dieserVorgang.suche.trim() || dieserVorgang.kategorie ? "Keine Treffer." : "Keine Dokumente vorhanden.";
        }
        if (kundeId == null) return "Kunde wählen, um seine Dokumente zu sehen.";
        if (kmvsReiter?.fehler) return null;
        if (kmvsReiter && kmvsReiter.zeilen.length === 0) {
            return kmvsReiter.laedt
                ? "Wird geladen…"
                : kmvsReiter.suche.trim() || kmvsReiter.kategorie
                  ? "Keine Treffer."
                  : reiter === "akte"
                    ? "In diesem Ordner liegen keine Dateien."
                    : "Keine Dokumente vorhanden.";
        }
        return null;
    })();

    const rechnerKarte = (() => {
        if (reiter !== "rechner") return null;
        if (liest) return <div className="dok-aw-hinweis">Wird eingelesen…</div>;
        if (hinzugefuegt.length > 0) return null;
        return (
            <div className={`dok-aw-ablagekarte${ziehen ? " drueber" : ""}`}>
                <span className="dok-aw-karte-icon">
                    <HochladenIcon />
                </span>
                <strong>Dateien oder Ordner hierher ziehen</strong>
                <span>oder im Explorer auswählen</span>
                <div className="dok-aw-karte-knoepfe">
                    <button
                        type="button"
                        className="dok-aw-knopf stark"
                        onClick={() => dateiFeld.current?.click()}
                    >
                        {einzeln ? "Datei auswählen…" : "Dateien auswählen…"}
                    </button>
                    {!einzeln && (
                        <button
                            type="button"
                            className="dok-aw-knopf"
                            onClick={() => ordnerFeld.current?.click()}
                        >
                            Ordner auswählen…
                        </button>
                    )}
                </div>
            </div>
        );
    })();

    const zeilenAnzahl = gruppen.reduce((n, g) => n + g.zeilen.length, 0);
    const namen = [...auswahl.values()].map((e) =>
        e.art === "dok"
            ? (e.doc.fileName ?? "").split("/").pop() || e.doc.fileName || ""
            : e.datei.name,
    );

    return (
        <div
            ref={wurzel}
            className="dok-aw"
            onKeyDown={(e) => {
                if (e.key !== "Escape" || e.defaultPrevented) return;
                e.stopPropagation();
                if (pickerOffen) setPickerOffen(false);
                else onAbbrechen();
            }}
            // Gezogene Dateien gehören der Auswahl: `stopPropagation`, sonst lädt
            // eine umgebende Drop-Zone (die To-do-Karte, `DateiDropZoneV4`) sie
            // SOFORT hoch — und „Hinzufügen" lädt sie ein zweites Mal
            // (PROD-Ticket T-16: jede Datei stand doppelt am To-do).
            onDragEnter={(e) => {
                if (e.dataTransfer.types.includes("Files")) e.stopPropagation();
            }}
            onDragOver={(e) => {
                if (!e.dataTransfer.types.includes("Files")) return;
                e.preventDefault();
                e.stopPropagation();
                setZiehen(true);
            }}
            onDragLeave={(e) => {
                if (e.dataTransfer.types.includes("Files")) e.stopPropagation();
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setZiehen(false);
            }}
            onDrop={(e) => {
                if (!e.dataTransfer.types.includes("Files")) return;
                e.preventDefault();
                e.stopPropagation();
                setZiehen(false);
                // Einträge JETZT holen — nach dem Ereignis ist die Liste leer.
                const eintraege = eintraegeAusDrop(e.dataTransfer);
                if (!eintraege) {
                    nimmDateien(ausDateiFeld(Array.from(e.dataTransfer.files ?? [])));
                    return;
                }
                setLiest(true);
                void ausDrop(eintraege)
                    .then(nimmDateien)
                    .finally(() => setLiest(false));
            }}
        >
            <div className="dok-aw-reiter" role="tablist">
                <ReiterKnopf
                    name="Kundendokumente"
                    aktiv={reiter === "kunde"}
                    zahl={kundeId != null ? kundenReiter.gesamt : null}
                    onClick={() => wechsleReiter("kunde")}
                />
                <ReiterKnopf
                    name="Kundenakte"
                    aktiv={reiter === "akte"}
                    zahl={null}
                    onClick={() => wechsleReiter("akte")}
                />
                <ReiterKnopf
                    name="Aus Vorgängen"
                    aktiv={reiter === "vorgaenge"}
                    zahl={vorgaengeZahl}
                    onClick={() => wechsleReiter("vorgaenge")}
                />
                <ReiterKnopf
                    name="Vom Rechner"
                    aktiv={reiter === "rechner"}
                    zahl={hinzugefuegt.length || null}
                    onClick={() => wechsleReiter("rechner")}
                />
                {!imFenster && (
                    <button
                        type="button"
                        className="dok-aw-schliessen"
                        title="Schließen"
                        aria-label="Schließen"
                        onClick={onAbbrechen}
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden {...linie} strokeWidth={1.9}>
                            <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" />
                        </svg>
                    </button>
                )}
            </div>

            {zeigeWerkzeuge && <div className="dok-aw-werkzeuge">
                {pfad}
                {kmvsReiter && reiter !== "akte" && hatListe && !pickerOffen && (
                    <select
                        className="dok-aw-kategorie"
                        value={kmvsReiter.kategorie}
                        onChange={(e) => setzeKategorie(e.target.value)}
                        aria-label="Kategorie"
                    >
                        {KATEGORIE_FILTER.map((k) => (
                            <option key={k.value} value={k.value}>
                                {k.text}
                            </option>
                        ))}
                    </select>
                )}
                {zeigeSuche && (
                    <label className="dok-aw-suche">
                        <SuchIcon />
                        <input
                            type="search"
                            value={suche}
                            onChange={(e) => setzeSuche(e.target.value)}
                            placeholder="Suchen"
                            aria-label="Suchen"
                        />
                    </label>
                )}
                {reiter === "rechner" && (
                    <>
                        <button
                            type="button"
                            className="dok-aw-knopf"
                            onClick={() => dateiFeld.current?.click()}
                        >
                            {einzeln ? "Datei…" : "Dateien…"}
                        </button>
                        {!einzeln && (
                            <button
                                type="button"
                                className="dok-aw-knopf"
                                onClick={() => ordnerFeld.current?.click()}
                            >
                                Ordner…
                            </button>
                        )}
                        <button type="button" className="dok-aw-knopf flach" onClick={listeLeeren}>
                            Liste leeren
                        </button>
                    </>
                )}
            </div>}

            <div className="dok-aw-koerper">
                <div className="dok-aw-liste">
                    {rechnerKarte}
                    {reiter === "akte" && kundeId != null && !pickerOffen && (
                        <div style={{ padding: "8px 10px 4px" }}>
                            <OrdnerNavigator contactId={kundeId} pfad={aktenPfad} onPfad={setAktenPfad} maxHoehe={170} />
                        </div>
                    )}
                    {kmvsReiter?.fehler && kundeId != null && (
                        <div className="dok-aw-fehler">{kmvsReiter.fehler}</div>
                    )}
                    {reiter === "vorgaenge" && dieserVorgang.fehler && !(kmvsReiter?.fehler && kundeId != null) && (
                        <div className="dok-aw-fehler">{dieserVorgang.fehler}</div>
                    )}
                    {hinweisOhneZeilen && <div className="dok-aw-hinweis">{hinweisOhneZeilen}</div>}
                    {zeilenAnzahl > 0 && (
                        <div
                            className="dok-aw-tabelle"
                            role="listbox"
                            aria-label="Dokumente"
                            aria-multiselectable={!einzeln}
                            aria-activedescendant={fokus ? zeilenId(fokus) : undefined}
                            tabIndex={0}
                            onKeyDown={onTaste}
                        >
                            <div className="dok-aw-kopfzeile" aria-hidden>
                                <span>Name</span>
                                <span>Geändert</span>
                                <span className="dok-aw-sp-typ">Typ</span>
                                <span className="dok-aw-sp-gr">Größe</span>
                            </div>
                            {gruppen.map((gruppe) => {
                                const zu = !!gruppe.titel && zugeklappt.has(gruppe.key);
                                return (
                                    <React.Fragment key={gruppe.key}>
                                        {gruppe.titel && (
                                            <button
                                                type="button"
                                                className={`dok-aw-gruppe${zu ? " zu" : ""}`}
                                                aria-expanded={!zu}
                                                tabIndex={-1}
                                                onClick={() =>
                                                    setZugeklappt((vorher) => {
                                                        const naechste = new Set(vorher);
                                                        if (naechste.has(gruppe.key)) {
                                                            naechste.delete(gruppe.key);
                                                        } else {
                                                            naechste.add(gruppe.key);
                                                        }
                                                        return naechste;
                                                    })
                                                }
                                            >
                                                <span className="dok-aw-chev">▾</span>
                                                {gruppe.titel}
                                                <span className="dok-aw-gruppe-titel2">
                                                    {gruppe.unterzeile
                                                        ? `${gruppe.unterzeile} · `
                                                        : ""}
                                                    {gruppe.zeilen.length}
                                                </span>
                                            </button>
                                        )}
                                        {!zu &&
                                            gruppe.zeilen.map((z) => (
                                                <ZeileAnsicht
                                                    key={z.key}
                                                    zeile={z}
                                                    gewaehlt={auswahl.has(z.key)}
                                                    fokus={fokus === z.key}
                                                    onKlick={(e) =>
                                                        waehle(z, {
                                                            strg: e.ctrlKey || e.metaKey,
                                                            umschalt: e.shiftKey,
                                                        })
                                                    }
                                                    onDoppelklick={() => {
                                                        if (z.sperre) return;
                                                        const m = einzeln
                                                            ? new Map<string, Eintrag>()
                                                            : new Map(auswahl);
                                                        m.set(z.key, z.eintrag);
                                                        onUebernehmen(ergebnis(m));
                                                    }}
                                                />
                                            ))}
                                    </React.Fragment>
                                );
                            })}
                            {kmvsReiter?.hatMehr && (
                                <button
                                    type="button"
                                    className="dok-aw-mehr"
                                    disabled={kmvsReiter.laedt}
                                    onClick={kmvsReiter.mehrLaden}
                                >
                                    {kmvsReiter.laedt
                                        ? "Wird geladen…"
                                        : `Mehr laden (${kmvsReiter.zeilen.length} von ${kmvsReiter.gesamt})`}
                                </button>
                            )}
                        </div>
                    )}
                    {reiter === "rechner" && zeilenAnzahl === 0 && rechnerSuche.trim() && (
                        <div className="dok-aw-hinweis">Keine Treffer.</div>
                    )}
                </div>
                {ziehen && <div className="dok-aw-ablage">Hier loslassen zum Hinzufügen</div>}
            </div>

            <input
                ref={dateiFeld}
                type="file"
                multiple={!einzeln}
                accept={accept}
                style={{ display: "none" }}
                onChange={(e) => {
                    nimmDateien(ausDateiFeld(Array.from(e.target.files ?? [])));
                    // Zurücksetzen, damit dieselbe Datei erneut gewählt werden
                    // kann — sonst feuert `change` beim zweiten Mal nicht.
                    e.target.value = "";
                }}
            />
            <input
                ref={ordnerFeld}
                type="file"
                multiple
                style={{ display: "none" }}
                // Ordnerauswahl im Explorer; React kennt das Attribut nicht als Prop-Typ.
                {...{ webkitdirectory: "", directory: "" }}
                onChange={(e) => {
                    nimmDateien(ausDateiFeld(Array.from(e.target.files ?? [])));
                    e.target.value = "";
                }}
            />

            <div className="dok-aw-fuss">
                <div className="dok-aw-anzahl" aria-live="polite">
                    {auswahl.size === 0 ? (
                        <span>
                            Nichts ausgewählt
                            <span className="dok-aw-anzahl-tipp"> · Doppelklick übernimmt direkt</span>
                        </span>
                    ) : (
                        <>
                            <span className="dok-aw-anzahl-zahl" aria-label={`${auswahl.size} ausgewählt`}>
                                {auswahl.size}
                            </span>
                            <span className="dok-aw-anzahl-namen" title={namen.join(", ")}>
                                {namen.join(", ")}
                            </span>
                        </>
                    )}
                </div>
                <button type="button" className="dok-aw-knopf flach" onClick={onAbbrechen}>
                    Abbrechen
                </button>
                <button
                    type="button"
                    className="dok-aw-knopf stark"
                    disabled={auswahl.size === 0}
                    onClick={uebernehmen}
                >
                    {uebernehmenText}
                </button>
            </div>
            {!imFenster && (
                <span
                    className="dok-aw-griff"
                    title="Größe ändern"
                    aria-hidden
                    onPointerDown={(e) => {
                        const huelle = wurzel.current?.closest<HTMLElement>(".dok-aw-huelle");
                        if (huelle) ziehGroesse(e, huelle);
                    }}
                />
            )}
        </div>
    );
};

// ── Größe ziehen ──────────────────────────────────────────────────────────
// Die Hüllen stehen mittig im Schleier. Wächst die Hülle um d, wandert ihre
// Ecke nur um d/2 — der Griff nimmt deshalb die DOPPELTE Mausbewegung, damit
// die Ecke unter dem Zeiger bleibt. Die Größe merkt sich der Browser je
// Nutzer (localStorage), damit sie beim nächsten Öffnen wieder stimmt.

const GROESSE_SCHLUESSEL = "kmvs.dokumentAuswahl.groesse";
const MIN_BREITE = 480;
const MIN_HOEHE = 360;

const leseGroesse = (): { b: number; h: number } | null => {
    try {
        const g = JSON.parse(localStorage.getItem(GROESSE_SCHLUESSEL) ?? "null");
        return g && Number.isFinite(g.b) && Number.isFinite(g.h) ? g : null;
    } catch {
        return null;
    }
};

const setzeGroesse = (huelle: HTMLElement, b: number, h: number) => {
    const breite = Math.round(Math.min(aktivesFenster().innerWidth - 32, Math.max(MIN_BREITE, b)));
    const hoehe = Math.round(Math.min(aktivesFenster().innerHeight - 32, Math.max(MIN_HOEHE, h)));
    huelle.style.width = `${breite}px`;
    huelle.style.height = `${hoehe}px`;
    huelle.style.maxHeight = "none";
    return { b: breite, h: hoehe };
};

const ziehGroesse = (e: React.PointerEvent, huelle: HTMLElement) => {
    e.preventDefault();
    const start = huelle.getBoundingClientRect();
    const x0 = e.clientX;
    const y0 = e.clientY;
    let zuletzt = { b: start.width, h: start.height };
    const bewege = (ev: PointerEvent) => {
        zuletzt = setzeGroesse(
            huelle,
            start.width + 2 * (ev.clientX - x0),
            start.height + 2 * (ev.clientY - y0),
        );
    };
    const ende = () => {
        alleFenster.removeEventListener("pointermove", bewege);
        alleFenster.removeEventListener("pointerup", ende);
        document.body.style.cursor = "";
        // Endet das Ziehen über dem Schleier, feuert dort ein Klick — und der
        // Schleier schließt bei Klick den Dialog. Den einen Klick schlucken.
        const schlucke = (ev: MouseEvent) => {
            ev.stopPropagation();
            ev.preventDefault();
        };
        alleFenster.addEventListener("click", schlucke, { capture: true, once: true });
        setTimeout(() => alleFenster.removeEventListener("click", schlucke, { capture: true }), 0);
        try {
            localStorage.setItem(GROESSE_SCHLUESSEL, JSON.stringify(zuletzt));
        } catch {
            /* Ohne Speicher gilt die Größe eben nur bis zum Schließen. */
        }
    };
    document.body.style.cursor = "nwse-resize";
    alleFenster.addEventListener("pointermove", bewege);
    alleFenster.addEventListener("pointerup", ende);
};

/** DOM-Id einer Zeile für `aria-activedescendant` — Schlüssel enthalten „/" und „:". */
const zeilenId = (key: string) => `dok-aw-z-${key.replace(/[^A-Za-z0-9_-]/g, "_")}`;

const ZeileAnsicht: React.FC<{
    zeile: Zeile;
    gewaehlt: boolean;
    fokus: boolean;
    onKlick: (e: React.MouseEvent) => void;
    onDoppelklick: () => void;
}> = ({ zeile: z, gewaehlt, fokus, onKlick, onDoppelklick }) => (
    <div
        id={zeilenId(z.key)}
        role="option"
        aria-selected={gewaehlt}
        aria-disabled={z.sperre != null}
        title={z.titel}
        className={["dok-aw-zeile", gewaehlt && "sel", fokus && "fokus", z.sperre && "gesperrt"]
            .filter(Boolean)
            .join(" ")}
        onMouseDown={(e) => {
            // Umschalt+Klick markierte sonst Text.
            if (e.shiftKey) e.preventDefault();
        }}
        onClick={onKlick}
        onDoubleClick={onDoppelklick}
    >
        <span className="dok-aw-name">
            <DateiIcon typ={z.typ} />
            <span className="dok-aw-name-text">{z.name}</span>
            {z.sperre === "dabei" && <span className="dok-aw-marke">schon angehängt</span>}
        </span>
        <span>{z.geaendert}</span>
        <span className="dok-aw-sp-typ">{z.typ}</span>
        <span className="dok-aw-sp-gr">{z.groesse}</span>
    </div>
);

const ReiterKnopf: React.FC<{
    name: string;
    aktiv: boolean;
    zahl: number | null;
    onClick: () => void;
}> = ({ name, aktiv, zahl, onClick }) => (
    <button
        type="button"
        role="tab"
        aria-selected={aktiv}
        className={aktiv ? "on" : undefined}
        onClick={onClick}
    >
        {name}
        {zahl != null && zahl > 0 && <span className="dok-aw-zahl">{zahl}</span>}
    </button>
);

// ── Icons ─────────────────────────────────────────────────────────────────

/** Farbe des Typ-Bands im Datei-Icon — an den Office-/PDF-Farben orientiert, in KMVS-Tönen. */
const TYP_FARBE: Record<string, string> = {
    PDF: "#c23b3b",
    Word: "#3a6ea5",
    Excel: "#2f7d5b",
    CSV: "#2f7d5b",
    PowerPoint: "#c8642a",
    "E-Mail": "#7d2169",
    JPG: "#b21f93",
    PNG: "#b21f93",
    GIF: "#b21f93",
    HEIC: "#b21f93",
    TIFF: "#b21f93",
    Bild: "#b21f93",
};

const DateiIcon: React.FC<{ typ: string }> = ({ typ }) => (
    <svg className="dok-aw-icon" width="18" height="18" viewBox="0 0 18 18" aria-hidden>
        <path
            d="M4 1.5h6.5l3.5 3.5v11a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5Z"
            fill="#fff"
            stroke="#ddd3dc"
        />
        <path d="M10.5 1.5V5H14" fill="none" stroke="#ddd3dc" />
        <rect x="2" y="9" width="10" height="5.5" rx="1.5" fill={TYP_FARBE[typ] ?? "#9a8c95"} />
        <text
            x="7"
            y="13.1"
            fontSize="3.8"
            fontWeight="700"
            fill="#fff"
            textAnchor="middle"
            fontFamily="sans-serif"
        >
            {typ.slice(0, 4).toUpperCase()}
        </text>
    </svg>
);

const linie = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
};

const HochladenIcon: React.FC = () => (
    <svg width="34" height="34" viewBox="0 0 24 24" aria-hidden {...linie} strokeWidth={1.6}>
        <path d="M7 18a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 18 8.5a4 4 0 0 1-.5 9.5" />
        <path d="M12 12v8m0-8-3 3m3-3 3 3" />
    </svg>
);

const SuchIcon: React.FC = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden {...linie} strokeWidth={2}>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
    </svg>
);

export default DokumentAuswahl;
